import * as React from "react";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { UnmountClosed } from "react-collapse";
import { ActionType } from "../../../../../react-core/Flux/ActionType";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { DepositSort } from "../../../../../Wrap/Constants/Enums/Domain/DepositSort";
import { SubAccountType } from "../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ServiceProposition } from "../../../../../Wrap/Constants/Enums/ServiceProposition";
import { SortDirection } from "../../../../../Wrap/Core/SortingAndPaging/SortDirection";
import { DepositProductsQuery } from "../../../../../Wrap/Models/Domains/Investment/DepositProductsQuery";
import { ResearchISTDispatcher } from "../../../../Dashboard/Components/ToolsAndLiterature/Store/Variable";
import { InvestmentSelectionAction } from "../../Action/InvestmentSelectionAction";
import { AllocationTableItem } from "../../Store/SelectedInvesmentStoreStatic";
import { DepositSearchResults } from "./DepositSearchResult";
import { DepositProductType } from "../../../../../Wrap/Constants/Enums/Domain/DepositProductType";
import { CashProductTermRange } from "../../../../../Wrap/Constants/Enums/Domain/CashProductTermRange";
import { BatchType } from "../../../../../Wrap/Constants/Enums/Orders/Batch/BatchType";

interface IDepositsSearchProps {
    hiercharyId: string;
    type: SubAccountType;
    tableData: AllocationTableItem[];
    index: number;
    researchIst: boolean;
    accountServiceProposition: ServiceProposition;
    canBeBought: boolean;
    canBeSold: boolean;
    CanBeTransferred?: boolean;
    batchType?: BatchType;
}

const SearchTypes = {
    //[DepositProductType.EasyAccess]: "Easy Access", - uncomment this when abrdn ask to bring Easy Access back into scope
    [DepositProductType.Notice]: "Notice",
    [DepositProductType.Term]: "Fixed term"
}
const SearchTerms = {
    [CashProductTermRange.LessThanOne]: "Less than 1 year",
    [CashProductTermRange.OneToTwo]: "1 to 2 years",
    [CashProductTermRange.TwoToThree]: "2 to 3 years",
    [CashProductTermRange.OverThree]: "Over 3 years"
}



export const DepositsSearch = (props: IDepositsSearchProps, ref: any) => {
    const [showResults, setShowResults] = useState(false);
    const [depositType, setDepositType] = useState(null);
    const [depositTerm, setDepositTerm] = useState(CashProductTermRange.Unspecified);
    const [data, setData] = useState(null);
    const [isSearching, setIsSearching] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredSearchTypes = props.batchType === BatchType.Standing
        ? { [DepositProductType.Notice]: "Notice" }
        : {
            [DepositProductType.Notice]: "Notice",
            [DepositProductType.Term]: "Fixed term"
        };

    useImperativeHandle(ref, () => ({
        close: () => {
            setShowResults(false);
        },
    }));

    useEffect(() => {
        setCurrentPage(1);
        searchCashDeposit(1);
    }, [depositType, depositTerm]);

    const searchCashDeposit = (pageIndex) => {
        if ((depositTerm == null || depositTerm === CashProductTermRange.Unspecified) && depositType == null)
        {
            setShowResults(false);
            return;
        }

        const query: DepositProductsQuery =
        {
            CashProductTermRange: depositTerm ?? CashProductTermRange.Unspecified,
            DepositProductType: depositType,
            CurrentPage: pageIndex,
            ProductWrapperTypes: [props.type],
            ServiceProposition: props.accountServiceProposition,
            Sorting: [{ Field: DepositSort.Rate, Direction: SortDirection.Descending }]
        };

        if (props.canBeBought)
        {
            query.CanBeBought = true;
        }
        if (props.canBeSold)
        {
            query.CanBeSold = true;
        }

        if (props.researchIst) {
            query.IsTemplate = true;
            query.ServiceProposition = ServiceProposition.Wrap;
            ResearchISTDispatcher.dispatch({
                actionType: ActionType.Update,
                payload: (curPage: number) => {
                    const _query = JSON.parse(JSON.stringify(query));
                    _query.CurrentPage = curPage;
                    getData(_query);
                },
            });
        }

        setIsSearching(true);

        getData(query);
    };

    const getData = (query) => {
        InvestmentSelectionAction.searchDepositProducts(query, props.hiercharyId, (data) => {
            setIsSearching(false);
            if (props.researchIst) {
                ResearchISTDispatcher.dispatch({ actionType: ActionType.Success, payload: data });
            } else {
                setData(data);
                showTableResults();
            }
        });
    };
    const showTableResults = () => {
        setShowResults(true);
    };

    return (
        <>
            <div className="ist-advanced-panel">
                <h3>Search</h3>
                <div className="row">
                    <div className="col-12 col-sm-6 col-md-4">
                        <SelectEditor
                            label="Deposit type"
                            placeholder="Please select"
                            options={Object.keys(filteredSearchTypes).map((key) => Number(key))}
                            getText={(o) => SearchTypes[o]}
                            onChange={v => setDepositType(v)}
                        />
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                        <SelectEditor
                            label="Term"
                            placeholder="Please select"
                            options={Object.keys(SearchTerms).map((v) => Number(v))}
                            getText={(o) => SearchTerms[o]}
                            onChange={v => setDepositTerm(v)}
                        />
                    </div>
                </div>
                <div className="row">
                    <p style={{fontSize: "12px"}}>
                        When choosing a cash deposit account(s) you should consider that the maximum protection provided
                        by the Financial Services Compensation Scheme (FSCS) is £85,000 per banking license. If your
                        client holds money in another account with the same bank as one of your chosen cash deposit
                        accounts, either on or outside of abrdn Wrap, this counts towards the total £85,000 compensation
                        available to your client from that bank.
                    </p>
                </div>
                {
                    !props.researchIst &&
                    (
                        <div className="row">
                            <p style={{fontSize: "12px", fontWeight: "bold"}}>
                                When investing in a cash deposit you must ensure that sufficient cash or investments
                                remain in your client's platform product(s) to pay any charges.
                            </p>
                        </div>
                    )
                }
            </div>

            <UnmountClosed isOpened={showResults}>
                {!props.researchIst && data && (
                    <DepositSearchResults
                        data={data}
                        allocationTable={props.tableData}
                        onPageChange={(i) => {
                            searchCashDeposit(i);
                        }}
                        index={currentPage}
                        researchIst={props.researchIst}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                    />
                )}
            </UnmountClosed>
        </>
    );
};

export default forwardRef(DepositsSearch);
